import React, {useEffect, useMemo, useState} from 'react';
import {Table, Button, Tag, Input, Modal, Empty, Spin, message, Select, Tooltip} from 'antd';
import {FlagOutlined, FilePdfOutlined, RedoOutlined, EditOutlined, DeleteOutlined} from '@ant-design/icons';
import axios from 'axios';
import { OpenInternalOrderData } from '../../types/OpenInternalOrderData';
import './OpenInternalOrders.css';
import LoadingModal from "../../components/loadingModal/LoadingModal";
import {StatusUtils} from "../../utils/StatusUtils";
import {TaskData} from "../../types/TaskData";
import {getConfig} from "../../config";
import SystemPlanningOrderCreation
    from "../internalOrders/systemPlanningOrder/systemPlanningOrderCreation/SystemPlanningOrderCreation";
import MeasureOrderCreation from "../internalOrders/measureOrder/measureOrderCreation/MeasureOrderCreation";
import ConstructionOrderCreation
    from "../internalOrders/constructionOrder/constructionOrderCreation/ConstructionOrderCreation";
import {ViewType} from "../../types/ViewType";
import {useProjectContext} from "../../context/ProjectContext";
import dayjs from "dayjs";

const OpenInternalOrders: React.FC = () => {
    const [data, setData] = useState<OpenInternalOrderData[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [heliosUrl, setHeliosUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [changeData, setChangeData] = useState<TaskData | null>(null);
    const [selectedRecordToChange, setSelectedRecordToChange] = useState<OpenInternalOrderData | null>(null);
    const [isChangeModalVisible, setIsChangeModalVisible] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [filters, setFilters] = useState<Record<string, string | undefined>>({});
    const [selectedPurchaseOrderNo, setSelectedPurchaseOrderNo] = useState<OpenInternalOrderData | null>(null); // Track which order to process
    const { API_BASE_URL } = getConfig();
    const productOptions = Array.from(new Set(data.map(d => d.product).filter(Boolean)));
    const activityOptions = useMemo(() => {return Array.from(new Set(data.map(d => d.activityName).filter(Boolean)));
    }, [data]);
    const statusOptions = ['DONE', 'READY', 'NOT_READY', 'PENDING'] as const;
    const { Option } = Select;

    const filteredData = useMemo(() => {
        return data.filter(record =>
            Object.entries(filters).every(([key, val]) => {
                if (!val) return true;
                const recordValue = (record as any)[key];

                if (key === "status") {
                    return recordValue === val; // exakter Vergleich
                }

                return (recordValue ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(val.toLowerCase());
            })
        );
    }, [data, filters]);

    useEffect(() => {
        setData([]);
        if (selectedProject) {
            fetchData(selectedProject);
        }
    }, [selectedProject]);

    const handleFilterChange = (value: string | undefined, columnKey: string) => {
        setFilters(prev => ({
            ...prev,
            [columnKey]: value,
        }));
    };

    const fetchData = (projectNo: string) => {
        setLoadingText("Daten werden geladen...");
        setLoading(true)
        axios
            .get<OpenInternalOrderData[]>(`${API_BASE_URL}/getOpenInternalOrders?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&managerNo=${selectedManagerNo}`)
            .then(response => setData(response.data))
            .catch(error => console.error('Error fetching data:', error))
            .finally(() => {
                setLoading(false); // Stop loading indicator regardless of success or error
            });
    };

    const handleOk = async () => {
        if (!selectedPurchaseOrderNo) return;
        setLoadingText("Wareneingang wird gebucht...");
        setIsModalVisible(false);
        setLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/bookNetworkGoodsReceivingOrder`, {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        networkPlanId: selectedPurchaseOrderNo.networkPlanId,
                        purchaseOrderNo: selectedPurchaseOrderNo.purchaseOrderNo,
                        heliosURL: heliosUrl?.trim() || null,
                    }
                }
            );
            if (response.data) {
                message.success('Wareneingang erfolgreich gebucht!');
                setLoading(false); // End loading
                setHeliosUrl('');
                if (selectedProject)    {
                    fetchData(selectedProject);
                }
            }
        } catch (error) {
            message.error('Fehler beim Buchen des Wareneingangs.');
            console.error('Error:', error);
            setLoading(false); // End loading
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setHeliosUrl('');
    };

    const handleInputChange = (value: string, setValue: (value: (((prevState: string) => string) | string)) => void) => {
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            setValue(value);
        } else  {
            message.error("Bitte gebe eine gültige Helios-URL an");
        }
    };

    const validateHeliosUrl = (value: string): boolean => {
        return value.startsWith("helios://") && value.length > "helios://".length;
    };

    const fetchPDFAndOpen = async (purchaseOrderNo: string) => {
        setLoadingText("PDF wird geladen...");
        setLoading(true); // Show loading spinner
        try {
            const response = await axios.get(
                `${API_BASE_URL}/getFullNetworkPlanOrderPDF?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&purchaseOrderNo=${purchaseOrderNo}`,
                { responseType: 'arraybuffer' } // Ensure we receive binary data
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobURL = URL.createObjectURL(blob);
            const pdfWindow = window.open(blobURL, '_blank');
            if (!pdfWindow) {
                message.error("Pop-up Blocker aktiv? Bitte erlaube Pop-ups für diese Seite.");
            }
        } catch (error) {
            message.error("Fehler beim Abrufen des PDFs.");
            console.error("Error fetching PDF:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (record: OpenInternalOrderData) => {
        Modal.confirm({
            title: 'Bist Du sicher?',
            content: 'Willst Du diese Bestellung wirklich löschen?',
            okText: 'Ja',
            cancelText: 'Nein',
            onOk: async () => {
                setLoadingText('Bestellung wird gelöscht...');
                setLoading(true);
                try {
                    await axios.get(
                        `${API_BASE_URL}/deleteOrder`,
                        {
                            params: {
                                auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                                orderNo: record.purchaseOrderNo,
                            }
                        }
                    );
                    message.success('Bestellung erfolgreich gelöscht.');
                    if (selectedProject)    {
                        fetchData(selectedProject);
                    }
                } catch (error) {
                    message.error('Fehler beim Löschen der Bestellung.');
                    console.error('Error deleting order:', error);
                } finally {
                    setLoading(false);
                }
            },
            onCancel: () => {
                message.info('Löschen abgebrochen.');
            },
        });
    };

    const fetchTaskData = async (record: OpenInternalOrderData) => {
        setLoading(true); // Show loading indicator
        try {
            const response = await axios.get<TaskData>(
                `${API_BASE_URL}/getSpecificNetworkTask`,
                {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        projectNo: record.projectNo,
                        type: 'systemPlanning',
                        networkPlanId: record.networkPlanId,
                    },
                }
            );
            setChangeData(response.data);
        } catch (error) {
            message.error('Fehler beim Laden der Systemplanungs-Daten.');
            console.error('Error fetching TaskData:', error);
        } finally {
            setLoading(false);
        }
    };

    const closeChangeModal = () => {
        setIsChangeModalVisible(false);
        setChangeData(null); // Reset data when modal closes
    };

    const handleChange = (record: OpenInternalOrderData) => {
        setSelectedRecordToChange(record);
        fetchTaskData(record);
        setIsChangeModalVisible(true);
    };

    const columns = [
        {
            title: (
                <div>
                    Bestellung
                    <Input
                        allowClear
                        placeholder="Filter Bestellung"
                        onChange={e => handleFilterChange(e.target.value, 'purchaseOrderNo')}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'purchaseOrderNo',
            key: 'purchaseOrderNo',
            render: (purchaseOrderNo: string) => (
                <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => fetchPDFAndOpen(purchaseOrderNo)}
                    type="primary"
                    size="small"
                >
                    {purchaseOrderNo}
                </Button>
            ),
            width: 150,
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                (a.purchaseOrderNo ?? '').localeCompare(b.purchaseOrderNo ?? ''),
        },
        ...(selectedProject === "Alle"
            ? [
                {
                    title: (
                        <div>
                            Projekt
                            <Input
                                allowClear
                                placeholder="Filter Projekt"
                                onChange={e => handleFilterChange(e.target.value, 'projectNo')}
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '100%' }}
                            />
                        </div>
                    ),
                    dataIndex: 'projectNo',
                    width: 200,
                    key: 'projectNo',
                    sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                        (a.projectNo ?? '').localeCompare(b.projectNo ?? ''),
                },
            ]
            : []),
        {
            title: (
                <div>
                    Produkt
                    <Select
                        allowClear
                        showSearch
                        placeholder="Filter Produkt"
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onChange={value => handleFilterChange(value, 'product')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        {productOptions.map(product => (
                            <Option key={product} value={product}>{product}</Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'product',
            width: 280,
            key: 'product',
            render: (text: string, record: OpenInternalOrderData) => (
                <>
                    {text}
                    {record.korrex && ` (Korrex)`}
                </>
            ),
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                (a.product ?? '').localeCompare(b.product ?? ''),
        },
        {
            title: (
                <div>
                    Protokoll-Bez.
                    <Input
                        allowClear
                        placeholder="Filter Protokoll"
                        onChange={e => handleFilterChange(e.target.value, 'protocolDesc')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'protocolDesc',
            key: 'protocolDesc',
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) => {
                const valueA = a.protocolDesc ?? '';
                const valueB = b.protocolDesc ?? '';
                return valueA.localeCompare(valueB);
            },
            render: (text: string) => {
                const value = text || '-';
                return value;
            },
            width: 250,
        },
        {
            title: (
                <div>
                    Vorgang
                    <Select
                        allowClear
                        showSearch
                        placeholder="Filter Vorgang"
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onChange={value => handleFilterChange(value, 'activityName')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        {activityOptions.map(name => (
                            <Option key={name} value={name}>{name}</Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'activityName',
            key: 'activityName',
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                (a.activityName ?? '').localeCompare(b.activityName ?? ''),
        },
        {
            title: (
                <div>
                    Eingeplant bei
                    <Input
                        allowClear
                        placeholder="Filter Eingeplant bei"
                        onChange={e => handleFilterChange(e.target.value, 'plannedOn')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'plannedOn',
            key: 'plannedOn',
            render: (text: string) => {
                return text?.trim() || '-';
            },
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                (a.plannedOn ?? '').localeCompare(b.plannedOn ?? ''),
        },
        {
            title: (
                <div>
                    Eingeplant von
                    <Input
                        allowClear
                        placeholder="Filter Eingeplant von"
                        onChange={e => handleFilterChange(e.target.value, 'plannedFrom')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'plannedFrom',
            key: 'plannedFrom',
            render: (text: string) => {
                return text?.trim() || '-';
            },
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) => {
                const dateA = a.plannedFrom ? dayjs(a.plannedFrom, "DD.MM.YYYY") : null;
                const dateB = b.plannedFrom ? dayjs(b.plannedFrom, "DD.MM.YYYY") : null;
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
            }
        },
        {
            title: (
                <div>
                    Eingeplant bis
                    <Input
                        allowClear
                        placeholder="Filter Eingeplant bis"
                        onChange={e => handleFilterChange(e.target.value, 'plannedTo')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'plannedTo',
            key: 'plannedTo',
            render: (text: string) => {
                return text?.trim() || '-';
            },
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) => {
                const dateA = a.plannedTo ? dayjs(a.plannedTo, "DD.MM.YYYY") : null;
                const dateB = b.plannedTo ? dayjs(b.plannedTo, "DD.MM.YYYY") : null;
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
            }
        },
        {
            title: (
                <div>
                    Gew. Liefertermin
                    <Input
                        allowClear
                        placeholder="Filter Termin"
                        onChange={e => handleFilterChange(e.target.value, 'confirmedDate')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'confirmedDate',
            key: 'confirmedDate',
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) => {
                const dateA = dayjs(a.confirmedDate, "DD.MM.YYYY");
                const dateB = dayjs(b.confirmedDate, "DD.MM.YYYY");
                const validA = dateA.isValid();
                const validB = dateB.isValid();
                if (!validA && !validB) return 0;
                if (!validA) return 1;
                if (!validB) return -1;
                return dateA.diff(dateB);
            }
        },
        {
            title: (
                <div>
                    Status
                    <Select
                        allowClear
                        showSearch
                        placeholder="Filter Status"
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onChange={value => handleFilterChange(value, 'status')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '140px' }}
                    >
                        {statusOptions.map(status => (
                            <Option key={status} value={status}>
                                {StatusUtils.getTranslatedStatus(status)}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'status',
            key: 'status',
            width: 160,
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
            sorter: (a: OpenInternalOrderData, b: OpenInternalOrderData) =>
                (a.status ?? '').localeCompare(b.status ?? ''),
        },
        {
            title: 'Aktion',
            className: 'align-top',
            key: 'actions',
            render: (_: any, record: OpenInternalOrderData) => (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div>
                            <Button
                                disabled={!record.done}
                                type="primary"
                                icon={<FlagOutlined />}
                                onClick={async () => {
                                    if (record.supplier === 'Messen Müssig AG') {
                                        setSelectedPurchaseOrderNo(record);
                                        setIsModalVisible(true);
                                    } else {
                                        setSelectedPurchaseOrderNo(record);
                                        await handleOk();
                                    }
                                }}
                                size="small"
                            >WE Buchen</Button>
                        </div>
                    <div>
                        <Tooltip title="Beauftragung löschen">
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            size="small"
                        />
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="Beauftragung bearbeiten">
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => handleChange(record)}
                            size="small"
                        />
                        </Tooltip>
                    </div>
                </div>
            ),
        },
    ];
    return (
        <div style={{ padding: '20px' }}>
            <LoadingModal
                open={loading}
                text={loadingText}
            />
            <Table
                columns={columns}
                dataSource={filteredData}
                locale={{
                    emptyText: (
                        <Empty
                            description="Keine offenen Bestellungen vorhanden"
                        />
                    ),
                }}
                rowKey={(record) => `${record.purchaseOrderNo}-${record.networkPlanId}`}
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
            />
            <Modal
                title={
                    selectedRecordToChange?.supplier?.toLowerCase().includes("konstruktion")
                        ? "Anpassung Konstruktion"
                        : selectedRecordToChange?.supplier?.toLowerCase().includes("messen")
                            ? "Anpassung Messen"
                            : "Anpassung Systemplanung"
                }
                open={isChangeModalVisible}
                onCancel={closeChangeModal}
                footer={null}
                width={800}
            >
                {changeData && (
                    <>
                        {selectedRecordToChange?.supplier?.toLowerCase().includes("konstruktion") && (
                            <ConstructionOrderCreation
                                data={Array.isArray(changeData) ? changeData : [changeData]}
                                viewType={ViewType.CHANGE}
                                onRefreshTable={() => {
                                    closeChangeModal();
                                    if (selectedProject)    {
                                        fetchData(selectedProject);
                                    }
                                }}
                            />
                        )}
                        {selectedRecordToChange?.supplier?.toLowerCase().includes("messen") && (
                            <MeasureOrderCreation
                                data={Array.isArray(changeData) ? changeData : [changeData]}
                                viewType={ViewType.CHANGE}
                                onRefreshTable={() => {
                                    closeChangeModal();
                                    if (selectedProject)    {
                                        fetchData(selectedProject);
                                    }
                                }}
                            />
                        )}
                        {selectedRecordToChange?.supplier?.toLowerCase().includes("systemplanung") && (
                            <SystemPlanningOrderCreation
                                data={changeData}
                                viewType={ViewType.CHANGE}
                                onRefreshTable={() => {
                                    closeChangeModal();
                                    if (selectedProject)    {
                                        fetchData(selectedProject);
                                    }
                                }}
                            />
                        )}
                    </>
                )}
            </Modal>
            <Modal
                title="Bitte Helios-URL eingeben"
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancel" onClick={handleCancel} style={{ backgroundColor: 'red', color: 'white' }}>
                        Abbrechen
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleOk}
                        disabled={!heliosUrl.trim()}
                    >
                        Wareneingang buchen
                    </Button>,
                ]}
            >
                <Input
                    placeholder="Helios-URL"
                    value={heliosUrl}
                    onChange={(e) => handleInputChange(e.target.value, setHeliosUrl)}
                />
            </Modal>
        </div>
    );
};

export default OpenInternalOrders;
